import { VFC, useContext, useEffect, useState } from "react";
// Libraries
import { isEmpty } from "lodash";
import * as Sentry from "@sentry/react";
import { useParams, useLocation } from "react-router-dom";
// Utils
import {
  setBUToLocalStorage,
  setBUTypeToLocalStorage,
  setLocationsCountToLocalStorage,
  clearLocalStorage,
  getBUFromLocalStorage,
  getBUTypeFromLocalStorage,
  setFeaturesListToLocalStorage,
} from "helpers/storage";
// Hooks
import {
  useEventRequest,
  useGetCurrentBusinessUnit,
  useGetBusinessUnits,
  useGetCurrentUserInfo,
  useStateSpecies,
} from "hooks";
// Api
import { clearApolloStorage } from "api/graphQL";
// Components
import { SnackBarNotification } from "components";

import { AppContext } from "config/appProvider";
import { CommonContext } from "config/commonProvider";

const DashboardWatcher: VFC = () => {
  const [hasSentAppStartup, setHasSentAppStartup] = useState(false);
  const { changeMainAppState, mainAppState, setFeaturesList } = useContext(AppContext);
  const { changeMainCommonState, mainCommonState, changeAnimalsFilter, resetAnimalsFilter, resetMainCommonState } =
    useContext(CommonContext);
  const { eventsRequest } = useEventRequest();

  const { data: businessUnits, loading } = useGetBusinessUnits();
  const { data: userInfo } = useGetCurrentUserInfo();

  const { buId } = useParams<DashboardPageParams>();
  const { pathname } = useLocation();

  const selectedBU = getBUFromLocalStorage();
  const selectedBUType = getBUTypeFromLocalStorage();

  const { locationsCount, foggles: buFeatures } = useGetCurrentBusinessUnit();

  const { autoHideDuration, isShow = false, variant = "", message = "" } = mainCommonState?.notification || {};

  useEffect(() => {
    if (buId && businessUnits && businessUnits.length > 0) {
      const selectedActual = businessUnits.find((item) => item?.id === buId);

      if (selectedActual) {
        changeMainAppState({
          ...mainAppState,
          selectedBU: +selectedActual.id,
          selectedBUType: selectedActual?.businessType?.slug || null,
          featuresList: selectedActual.foggles,
        });
        setBUToLocalStorage(selectedActual.id);

        if (selectedActual?.businessType?.slug) {
          setBUTypeToLocalStorage(selectedActual.businessType.slug);
        }
        if (selectedActual.foggles) {
          setFeaturesListToLocalStorage(selectedActual.foggles.toString());
        }
      }
    }
  }, [buId, businessUnits.length]);

  useEffect(() => {
    if (locationsCount != null) {
      setLocationsCountToLocalStorage(`${locationsCount}`);
    }
  }, [locationsCount]);

  useEffect(() => {
    if (userInfo?.userInfo && mainAppState.token && selectedBU && businessUnits.length) {
      if (!hasSentAppStartup) {
        eventsRequest({ name: "app-startup" });
        const { firstName, lastName, email } = userInfo.userInfo;
        const activeBusinessUnit = businessUnits.find((item) => item?.id === String(selectedBU));
        const farmAnimalTypes = activeBusinessUnit?.farmAnimalTypes.map((species) => species.name);
        const subscriptionPlan =
          activeBusinessUnit?.effectiveSubscriptionPlan != null
            ? activeBusinessUnit.effectiveSubscriptionPlan.name
            : null;

        // @ts-ignore
        pendo.initialize({
          visitor: {
            id: email,
            firstName,
            lastName,
            businessUnitName: activeBusinessUnit?.name,
            postcode: activeBusinessUnit?.postcode,
            country: activeBusinessUnit?.country?.name,
            farmAnimalTypes,
            subscriptionPlan,
          },
          account: {
            id: `${activeBusinessUnit?.id}-${activeBusinessUnit?.name}`,
          },
        });
        Sentry.setUser({ UserId: userInfo?.userInfo?.id });
        setHasSentAppStartup(true);
      }
    }
  }, [userInfo?.userInfo?.id, mainAppState.token, eventsRequest, selectedBU, businessUnits.length]);

  useEffect(() => {
    if (
      !isEmpty(mainCommonState.animalsFilters) &&
      !pathname.includes("animal") &&
      !pathname.includes("group/") &&
      !pathname.includes("batch/") &&
      !pathname.includes("location/") &&
      !pathname.includes("offer/") &&
      !pathname.includes("listing") &&
      !pathname.includes("archive")
    ) {
      resetAnimalsFilter();
    }
  }, [pathname, mainCommonState, changeAnimalsFilter]);

  const { active: speciesActive } = useStateSpecies();
  useEffect(() => {
    const isAnimalsFiltersEmpty = isEmpty(mainCommonState.animalsFilters);
    const isAnimalsFiltersValuesEmpty = Object.values(mainCommonState.animalsFilters).every(
      (value) => value === "" || value === null,
    );
    if (!isAnimalsFiltersEmpty && !isAnimalsFiltersValuesEmpty) {
      resetAnimalsFilter();
    }
  }, [speciesActive]);

  useEffect(() => {
    if (buFeatures) {
      setFeaturesListToLocalStorage(buFeatures.toString());
      setFeaturesList(buFeatures);
    }
  }, [buFeatures]);

  const logOut = (): void => {
    clearLocalStorage();
    // @ts-ignore
    changeMainAppState({});
    resetMainCommonState();
    clearApolloStorage();
    Sentry.configureScope((scope) => scope.setUser(null));
  };

  if (!loading && businessUnits.length === 0) {
    logOut();
  }

  if (!mainAppState.selectedBU && businessUnits && businessUnits.length > 0) {
    const isSelectedActual = !!businessUnits.find((item) => Number(item?.id) === selectedBU);

    if (!isSelectedActual) {
      setBUToLocalStorage(businessUnits[0]?.id || "");
      setBUTypeToLocalStorage(businessUnits[0]?.businessType?.slug || "");
      if (businessUnits[0]?.foggles) {
        setFeaturesListToLocalStorage(businessUnits[0].foggles.toString());
      }
      changeMainAppState({
        ...mainAppState,
        selectedBU: Number(businessUnits?.[0]?.id),
        selectedBUType: businessUnits?.[0]?.businessType?.slug || "",
        featuresList: businessUnits[0]?.foggles,
      });
    } else {
      changeMainAppState({
        ...mainAppState,
        selectedBU,
        selectedBUType,
      });
    }
  }

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        eventsRequest({
          name: "app-activate",
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return (): void => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [eventsRequest]);

  return (
    <SnackBarNotification
      autoHideDuration={autoHideDuration}
      open={isShow || false}
      variant={variant || "default"}
      message={message || ""}
      onClose={(): void =>
        changeMainCommonState({
          ...mainCommonState,
          notification: null,
        })
      }
    />
  );
};

export default DashboardWatcher;
